<template>
  <basic-page title="Ensivaste-ohjeet" :fluid="true" class="ensivaste-page">
    <v-row :no-gutters="$vuetify.breakpoint.xsOnly">
      <v-col
        cols="12"
        lg="8"
        offset-lg="2"
        xl="8"
        offset-xl="2"
        :class="$vuetify.breakpoint.xsOnly ? 'pa-0' : 'pa-3'"
      >
        <!-- Banner & Search -->
        <v-card
          :class="
            $vuetify.breakpoint.xsOnly
              ? 'pa-2 rounded-lg mb-2'
              : 'pa-4 rounded-xl mb-4'
          "
          class="elevation-2 primary white--text"
        >
          <v-row align="center" :dense="$vuetify.breakpoint.xsOnly">
            <v-col cols="12" md="8" :class="$vuetify.breakpoint.xsOnly ? 'py-1' : ''">
              <div class="d-flex align-center mb-1">
                <v-icon
                  :large="!$vuetify.breakpoint.xsOnly"
                  :small="$vuetify.breakpoint.xsOnly"
                  color="white"
                  class="mr-2"
                >
                  mdi-heart-pulse
                </v-icon>
                <div>
                  <h1
                    :class="
                      $vuetify.breakpoint.xsOnly ? 'text-subtitle-1' : 'text-h5'
                    "
                    class="font-weight-bold white--text"
                  >
                    Ensivasteen toiminta- ja kertausohjeet
                  </h1>
                  <div class="text-caption grey--text text--lighten-3">
                    Satakunnan ensivasteohjeistus (Minna Huuhka / Satakunnan shp &amp; pelastuslaitos)
                  </div>
                </div>
              </div>
              <p
                :class="$vuetify.breakpoint.xsOnly ? 'text-caption' : 'text-body-2'"
                class="white--text mt-1 mb-0"
              >
                Pikaopas ja kertaus ajomatkalle ja kohteessa toimimiseen: mitä kysytään, mitä mitataan ja mitä tehdään.
              </p>
            </v-col>

            <v-col cols="12" md="4" :class="$vuetify.breakpoint.xsOnly ? 'py-1' : ''">
              <v-text-field
                v-model="searchQuery"
                append-icon="mdi-magnify"
                label="Hae koodilla tai oireella (esim. 704, tajuton)..."
                solo
                light
                clearable
                hide-details
                dense
                class="rounded-lg"
              />
            </v-col>
          </v-row>
        </v-card>

        <!-- Tool Bar / Sub navigation buttons -->
        <div
          :class="$vuetify.breakpoint.xsOnly ? 'mb-2' : 'mb-4'"
          class="d-flex flex-wrap align-center"
        >
          <v-chip-group
            v-model="selectedCategory"
            mandatory
            active-class="primary white--text"
            class="py-0"
          >
            <v-chip filter value="all" :small="$vuetify.breakpoint.xsOnly" class="font-weight-medium">Kaikki tehtävät</v-chip>
            <v-chip filter value="resuscitation" :small="$vuetify.breakpoint.xsOnly" class="font-weight-medium">Elottomuus (700)</v-chip>
            <v-chip filter value="breathing" :small="$vuetify.breakpoint.xsOnly" class="font-weight-medium">Hengitys (703, 711)</v-chip>
            <v-chip filter value="circulation" :small="$vuetify.breakpoint.xsOnly" class="font-weight-medium">Sydän (704)</v-chip>
            <v-chip filter value="neurological" :small="$vuetify.breakpoint.xsOnly" class="font-weight-medium">Tajunta &amp; AVH (702, 706, 772)</v-chip>
            <v-chip filter value="trauma" :small="$vuetify.breakpoint.xsOnly" class="font-weight-medium">Vammat (74x)</v-chip>
            <v-chip filter value="medical" :small="$vuetify.breakpoint.xsOnly" class="font-weight-medium">Muut (705, 752...)</v-chip>
          </v-chip-group>

          <v-spacer />

          <div
            :class="$vuetify.breakpoint.xsOnly ? 'mt-1' : 'mt-2 mt-sm-0'"
            class="d-flex flex-wrap"
          >
            <v-btn
              color="primary"
              :small="!$vuetify.breakpoint.xsOnly"
              :x-small="$vuetify.breakpoint.xsOnly"
              outlined
              :class="$vuetify.breakpoint.xsOnly ? 'mr-1 mb-1' : 'ma-1'"
              class="font-weight-bold"
              @click="dialogGcs = true"
            >
              <v-icon left :x-small="$vuetify.breakpoint.xsOnly" :small="!$vuetify.breakpoint.xsOnly">mdi-calculator</v-icon>
              GCS-laskuri
            </v-btn>

            <v-btn
              color="primary"
              :small="!$vuetify.breakpoint.xsOnly"
              :x-small="$vuetify.breakpoint.xsOnly"
              outlined
              :class="$vuetify.breakpoint.xsOnly ? 'mr-1 mb-1' : 'ma-1'"
              class="font-weight-bold"
              @click="dialogFpss = true"
            >
              <v-icon left :x-small="$vuetify.breakpoint.xsOnly" :small="!$vuetify.breakpoint.xsOnly">mdi-brain</v-icon>
              FPSS-mittari
            </v-btn>

            <v-btn
              color="primary"
              :small="!$vuetify.breakpoint.xsOnly"
              :x-small="$vuetify.breakpoint.xsOnly"
              outlined
              :class="$vuetify.breakpoint.xsOnly ? 'mr-1 mb-1' : 'ma-1'"
              class="font-weight-bold"
              @click="dialogIsbar = true"
            >
              <v-icon left :x-small="$vuetify.breakpoint.xsOnly" :small="!$vuetify.breakpoint.xsOnly">mdi-phone-in-talk</v-icon>
              iSBAR-malli
            </v-btn>

            <v-btn
              color="primary"
              :small="!$vuetify.breakpoint.xsOnly"
              :x-small="$vuetify.breakpoint.xsOnly"
              outlined
              :class="$vuetify.breakpoint.xsOnly ? 'mr-1 mb-1' : 'ma-1'"
              class="font-weight-bold"
              @click="dialogMeds = true"
            >
              <v-icon left :x-small="$vuetify.breakpoint.xsOnly" :small="!$vuetify.breakpoint.xsOnly">mdi-pill</v-icon>
              Lääkkeet
            </v-btn>
          </div>
        </div>

        <!-- Notification if search active -->
        <v-alert
          v-if="searchQuery"
          dense
          outlined
          type="info"
          :class="$vuetify.breakpoint.xsOnly ? 'mb-2 pa-2 text-caption' : 'mb-3'"
        >
          Näytetään hakutulokset haulle "<strong>{{ searchQuery }}</strong>" ({{ filteredOhjeet.length }} kpl).
        </v-alert>

        <!-- Codes Expansion Panels -->
        <v-expansion-panels
          v-model="expandedPanels"
          multiple
          :class="$vuetify.breakpoint.xsOnly ? 'elevation-0 rounded-lg' : 'elevation-1 rounded-xl'"
        >
          <v-expansion-panel
            v-for="ohje in filteredOhjeet"
            :key="ohje.code"
            :class="$vuetify.breakpoint.xsOnly ? 'mb-1 rounded-lg' : 'mb-2 rounded-lg'"
            class="overflow-hidden"
          >
            <v-expansion-panel-header
              :class="[
                $vuetify.breakpoint.xsOnly ? 'py-1 px-2' : 'py-3 px-4',
                $vuetify.theme.dark ? 'ensivaste-header-dark' : 'grey lighten-4',
              ]"
            >
              <div class="d-flex align-center flex-wrap">
                <v-chip
                  color="primary"
                  dark
                  label
                  :small="$vuetify.breakpoint.xsOnly"
                  class="font-weight-bold mr-2 my-1"
                >
                  {{ ohje.codes ? ohje.codes.join(", ") : ohje.code }}
                </v-chip>
                <span
                  :class="[
                    $vuetify.breakpoint.xsOnly
                      ? 'text-body-2 font-weight-bold'
                      : 'text-subtitle-1 font-weight-bold',
                    $vuetify.theme.dark ? 'white--text' : 'grey--text text--darken-4',
                  ]"
                  class="mr-2"
                >
                  {{ ohje.title }}
                </span>
                <v-chip
                  x-small
                  outlined
                  :color="$vuetify.theme.dark ? 'grey lighten-1' : 'grey darken-2'"
                  class="mr-2 my-1"
                >
                  {{ ohje.categoryLabel }}
                </v-chip>
                <v-chip
                  v-if="ohje.urgency"
                  x-small
                  color="red darken-2"
                  text-color="white"
                  class="font-weight-bold mr-2 my-1"
                >
                  {{ ohje.urgency }}
                </v-chip>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content :class="$vuetify.breakpoint.xsOnly ? 'pt-1' : 'pt-3'">
              <ensivaste-quick-guide :code="ohje.code" inline />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- No results -->
        <v-sheet
          v-if="filteredOhjeet.length === 0"
          :class="$vuetify.theme.dark ? 'grey darken-4 white--text' : 'grey lighten-4'"
          class="pa-8 text-center rounded-xl mt-4"
        >
          <v-icon large color="grey">mdi-file-search-outline</v-icon>
          <div class="text-h6 grey--text text--darken-2 mt-2">
            Ei hakutuloksia kriteereillä
          </div>
          <v-btn
            color="primary"
            text
            class="mt-2"
            @click="
              searchQuery = '';
              selectedCategory = 'all';
            "
          >
            Tyhjennä suodatus
          </v-btn>
        </v-sheet>

        <!-- Muut yleiset muistikortit (ABCDE, RiVaLAiSeR, START) -->
        <v-row :class="$vuetify.breakpoint.xsOnly ? 'mt-2' : 'mt-6'" :dense="$vuetify.breakpoint.xsOnly">
          <v-col cols="12" md="4" :class="$vuetify.breakpoint.xsOnly ? 'pa-1' : ''">
            <v-card outlined :class="$vuetify.breakpoint.xsOnly ? 'rounded-lg mb-2' : 'rounded-xl h-100 elevation-1'">
              <v-card-title class="primary white--text py-2">
                <v-icon color="white" left small>mdi-format-list-numbered</v-icon>
                <span class="white--text">ABCDE-ensiarvio</span>
              </v-card-title>
              <v-card-text :class="$vuetify.breakpoint.xsOnly ? 'pa-2 pt-2 text-caption' : 'pt-3 text-body-2'">
                <p><strong>A (Airway / Ilmatiet):</strong> Ilmatien avoimuus, leuan nosto, pään asento, nielutuubi, kaularangan tuki.</p>
                <p><strong>B (Breathing / Hengitys):</strong> Hengitystaajuus (HT 10–25/min), happisaturaatio SpO2 (&ge; 95 %), rintakehän liike, lisähappi varaajamaskilla.</p>
                <p><strong>C (Circulation / Verenkierto):</strong> Rannesyke, verenpaine (RR), ääreisverenkierron lämpö, suuret ulkoiset vuodot tyrehdyksiin heti!</p>
                <p class="mb-2"><strong>D (Disability / Tajunta):</strong> Tajunnan taso GCS-pisteinä (3–15 p), pupillit (silmäterät), puhekyky ja <strong>verensokeri (B-Gluk)</strong>.</p>
                <p class="mb-2"><strong>E (Exposure / Paljastaminen):</strong> Vammojen etsintä, kehon lämpö, jäähtymisen (hypotermian) esto.</p>

                <v-divider class="my-2" />

                <div class="pa-2 rounded red lighten-5 red--text text--darken-4">
                  <div class="font-weight-bold d-flex align-center mb-1">
                    <v-icon x-small color="red darken-4" class="mr-1">mdi-water-alert</v-icon>
                    cABCDE (Kriittinen verenvuoto):
                  </div>
                  <div>
                    <strong>c (Catastrophic bleeding):</strong> Henkeä uhkaava suuri ulkoinen verenvuoto tyrehdytetään <strong>VÄLITTÖMÄSTI</strong> ennen muita vaiheita (kiristysside raajaan / suora voimakas painaminen / hemostaattinen side). Vasta vuodon ollessa hallinnassa edetään järjestelmällisesti A &rarr; B &rarr; C &rarr; D &rarr; E.
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4" :class="$vuetify.breakpoint.xsOnly ? 'pa-1' : ''">
            <v-card outlined :class="$vuetify.breakpoint.xsOnly ? 'rounded-lg mb-2' : 'rounded-xl h-100 elevation-1'">
              <v-card-title class="primary darken-1 white--text py-2">
                <v-icon color="white" left small>mdi-human</v-icon>
                <span class="white--text">RiVaLAiSeR (Vammatutkimus)</span>
              </v-card-title>
              <v-card-text :class="$vuetify.breakpoint.xsOnly ? 'pa-2 pt-2 text-caption' : 'pt-3 text-body-2'">
                <p><strong>Ri (Rintakehä):</strong> Kylkiluiden sively, murtumat, epäsymmetria, varstarinta (vastakkainen hengitysliike).</p>
                <p><strong>Va (Vatsa):</strong> Kämmentunnustelu kivuttomasta kipeään, pinkeys, mustelmat, arkuus.</p>
                <p><strong>L (Lantio):</strong> <span class="red--text font-weight-bold">ÄLÄ PAINA KÄSIN!</span> Etsi silmin virheasentoja tai jalan lyhentymää/kiertymää.</p>
                <p><strong>Ai (Aivot/Kallo/Kaula):</strong> Haavat, painumat, silmäterät, kaularangan aristus.</p>
                <p><strong>Se (Selkäranka):</strong> Tunnustelu rankaa yhtenä pakettina tukien kääntäessä.</p>
                <p class="mb-0"><strong>R (Raajat):</strong> Murtumat, asennot, ääreissyke (pulssit), tunto ja liikkuvuus.</p>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4" :class="$vuetify.breakpoint.xsOnly ? 'pa-1' : ''">
            <v-card outlined :class="$vuetify.breakpoint.xsOnly ? 'rounded-lg mb-2' : 'rounded-xl h-100 elevation-1'">
              <v-card-title class="grey darken-3 white--text py-2">
                <v-icon color="white" left small>mdi-traffic-light</v-icon>
                <span class="white--text">START-triage (Monipotilastilanne)</span>
              </v-card-title>
              <v-card-text :class="$vuetify.breakpoint.xsOnly ? 'pa-2 pt-2 text-caption' : 'pt-3 text-body-2'">
                <p><span class="green--text text--darken-3 font-weight-bold">● VIHREÄ (Lievä):</span> Kaikki kävelemään kykenevät potilaat.</p>
                <p><span class="amber--text text--darken-3 font-weight-bold">● KELTAINEN (Viivytettävä):</span> Ei kävele, mutta hengitystaajuus (HT) 10–30, rannesyke tuntuu ja noudattaa kehotuksia.</p>
                <p><span class="red--text text--darken-3 font-weight-bold">● PUNAINEN (Välitön):</span> Välitön hengenvaara! HT &lt; 10 tai &gt; 30 /min, rannesyke puuttuu tai ei noudata kehotuksia.</p>
                <p class="mb-0"><strong>● MUSTA (Menehtynyt):</strong> Ei hengitä ilmatien avaamisenkaan jälkeen, kaulavaltimon syke puuttuu.</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- GCS Wizard Dialog -->
    <gcs-wizard v-model="dialogGcs" />

    <!-- FPSS Wizard Dialog -->
    <fpss-wizard v-model="dialogFpss" />


    <!-- iSBAR Dialog -->
    <v-dialog
      v-model="dialogIsbar"
      max-width="700px"
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-card class="rounded-xl">
        <v-card-title class="primary white--text py-3">
          <v-icon color="white" class="mr-2">mdi-phone-in-talk</v-icon>
          <span class="white--text">iSBAR-konsultaatio- ja raportointimalli</span>
          <v-spacer />
          <v-btn icon dark @click="dialogIsbar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-2 mb-3">
            Käytä tätä runkoa konsultoidessasi lääkäriyksikköä (ESA 00) tai kenttäjohtajaa (L4) tai antaessasi suullisen raportin ambulanssille:
          </p>
          <v-card
            v-for="step in isbarMalli.steps"
            :key="step.letter"
            outlined
            class="pa-3 mb-2 rounded-lg grey lighten-5"
          >
            <div class="font-weight-bold text-subtitle-2 primary--text d-flex align-center">
              <v-avatar color="primary" size="24" class="white--text font-weight-bold mr-2">
                {{ step.letter }}
              </v-avatar>
              {{ step.name }}
            </div>
            <div class="text-body-2 mt-1 grey--text text--darken-3 pl-8">
              {{ step.content }}
            </div>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Lääkkeet Dialog -->
    <v-dialog
      v-model="dialogMeds"
      max-width="800px"
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-card class="rounded-xl">
        <v-card-title class="primary white--text py-3">
          <v-icon color="white" class="mr-2">mdi-pill</v-icon>
          <span class="white--text">Ensivasteen lääkkeet ja antoperiaatteet</span>
          <v-spacer />
          <v-btn icon dark @click="dialogMeds = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col v-for="(med, idx) in ensivasteLaakkeet" :key="idx" cols="12">
              <v-card outlined class="rounded-xl pa-3 mb-3 grey lighten-5">
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="text-subtitle-1 font-weight-bold primary--text">
                    {{ med.name }}
                  </span>
                  <v-chip small color="primary" dark class="font-weight-bold">
                    {{ med.dose }}
                  </v-chip>
                </div>
                <div class="text-body-2 mb-1"><strong>Antotapa:</strong> {{ med.route }}</div>
                <div
                  v-if="med.maskType"
                  class="text-body-2 mb-2 pa-2 rounded red lighten-5"
                >
                  <strong class="primary--text d-flex align-center">
                    <v-icon small color="primary" class="mr-1">mdi-face-mask</v-icon>
                    Maskin tyyppi:
                  </strong>
                  <div
                    class="grey--text text--darken-4 pl-4 mt-1"
                    style="white-space: pre-line;"
                  >
                    {{ med.maskType }}
                  </div>
                </div>
                <div
                  v-if="med.flowRate"
                  class="text-body-2 mb-2 pa-2 rounded grey lighten-4"
                >
                  <strong class="primary--text d-flex align-center">
                    <v-icon small color="primary" class="mr-1">mdi-speedometer</v-icon>
                    Litramäärä / Virtausnopeus:
                  </strong>
                  <div
                    class="grey--text text--darken-4 pl-4 mt-1"
                    style="white-space: pre-line;"
                  >
                    {{ med.flowRate }}
                  </div>
                </div>
                <div class="text-body-2 mb-1"><strong>Käyttöaihe:</strong> {{ med.indications }}</div>
                <div class="text-body-2 mb-1"><strong>Vaikutus:</strong> {{ med.effects }}</div>
                <div class="text-body-2 red--text text--darken-3 mb-1">
                  <strong>Vasta-aiheet:</strong> {{ med.contraindications }}
                </div>
                <div class="text-caption grey--text text--darken-2"><strong>Ohje:</strong> {{ med.notes }}</div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </basic-page>
</template>

<script>
import BasicPage from "@/components/BasicPage";
import EnsivasteQuickGuide from "@/components/Ensivaste/EnsivasteQuickGuide";
import GcsWizard from "@/components/Ensivaste/GcsWizard";
import FpssWizard from "@/components/Ensivaste/FpssWizard";
import {
  ENSIVASTE_OHJEET,
  ENSIVASTE_LAAKKEET,
  ISBAR_MALLI,
} from "@/assets/ensivasteOhjeet";

export default {
  name: "EnsivasteOhjeet",
  components: {
    BasicPage,
    EnsivasteQuickGuide,
    GcsWizard,
    FpssWizard,
  },
  data() {
    return {
      searchQuery: "",
      selectedCategory: "all",
      expandedPanels: [0],
      dialogGcs: false,
      dialogFpss: false,
      dialogIsbar: false,
      dialogMeds: false,

      isbarMalli: ISBAR_MALLI,
      ensivasteLaakkeet: ENSIVASTE_LAAKKEET,
      ensivasteOhjeet: ENSIVASTE_OHJEET,
    };
  },
  computed: {
    filteredOhjeet() {
      let list = this.ensivasteOhjeet;

      if (this.selectedCategory !== "all") {
        if (this.selectedCategory === "resuscitation") {
          list = list.filter((o) => o.category === "resuscitation");
        } else if (this.selectedCategory === "breathing") {
          list = list.filter((o) => o.category === "breathing");
        } else if (this.selectedCategory === "circulation") {
          list = list.filter((o) => o.category === "circulation");
        } else if (this.selectedCategory === "neurological") {
          list = list.filter(
            (o) => o.category === "neurological" || o.category === "consciousness"
          );
        } else if (this.selectedCategory === "trauma") {
          list = list.filter(
            (o) => o.category === "trauma" || o.category === "violence"
          );
        } else if (this.selectedCategory === "medical") {
          list = list.filter((o) =>
            [
              "general",
              "poisoning",
              "metabolic",
              "allergic",
              "obstetric",
              "violence",
            ].includes(o.category)
          );
        }
      }

      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter((o) => {
          const matchCode = o.codes && o.codes.some((c) => c.toLowerCase().includes(q));
          const matchTitle = o.title.toLowerCase().includes(q);
          const matchShort = o.shortSummary.toLowerCase().includes(q);
          const matchCategory = o.categoryLabel.toLowerCase().includes(q);
          return matchCode || matchTitle || matchShort || matchCategory;
        });
      }

      return list;
    },
  },
  mounted() {
    // Jos reitissä annettu ?code=704, asetetaan haku tai laajennetaan kyseinen paneeli
    if (this.$route.query.code) {
      this.searchQuery = this.$route.query.code;
      this.expandedPanels = [0];
    }
  },
};
</script>

<style scoped>
.ensivaste-header-dark {
  background-color: #242424 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.ensivaste-page ::v-deep .v-expansion-panel--active > .ensivaste-header-dark {
  background-color: #2c2c2c !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

@media (max-width: 600px) {
  .ensivaste-page ::v-deep .v-main > .v-container > .v-col {
    padding-left: 2px !important;
    padding-right: 2px !important;
    padding-top: 4px !important;
  }

  .ensivaste-page ::v-deep .v-expansion-panel-content__wrap {
    padding: 2px 2px 4px 2px !important;
  }

  .ensivaste-page ::v-deep .v-expansion-panel-header {
    min-height: 40px !important;
    padding: 6px 8px !important;
  }
}
</style>

